import { DeleteDialog, useSnackbar } from "@sagecentral/core-ui";
import { ValidationSummary } from "components/shared";
import { useCentralApi, useCurrentUser } from "hooks/shared";
import { useState } from "react";

export type UnrelateWorkItemSignature = (id: number) => void;

type UseUnrelateWorkItemResult = [
    UnrelateWorkItemSignature,
    React.ReactNode
];

type UseUnrelateWorkItemSignature = (
    workItemId: number,
    onUnrelated: () => void,
    draft?: boolean
) => UseUnrelateWorkItemResult;

const useUnrelateWorkItem: UseUnrelateWorkItemSignature = (
    workItemId,
    onUnrelated,
    draft
) => {
    const [ unrelateState, setUnrelateState ] = useState({
        id: 0,
        show: false
    });
    const [ working, setWorking ] = useState(false);
    const { displaySnackbar } = useSnackbar();
    
    const user = useCurrentUser();
    const fetcher = useCentralApi(user);

    const unrelateEntry = (id: number) => {
        setUnrelateState({
            id,
            show: true
        });
    };

    const closeUnrelate = () => {
        setUnrelateState({
            id: 0,
            show: false
        });
    };

    const confirmUnrelate = () => {
        if (!user || !user.access_token) return;
        if (unrelateState.id === 0) return;
        
        (async () => { 
            try {
                setWorking(true);
                const id = unrelateState.id;
                const url = draft 
                    ? `/api/v3/drafts/workitems/${workItemId}/related/${id}/unrelate`
                    : `/api/v3/workitems/${workItemId}/related/${id}/unrelate`;
                const resp = await fetcher(url, {
                    method: 'PATCH'
                }, async r => {                    
                    if (r.status === 400) {
                        const data = await r.json();
                        displaySnackbar('warning', <ValidationSummary validationDetail={data} />);
                    } else {
                        displaySnackbar('error', "We couldn't unrelate the work item.");
                    }
    
                    return true;
                });
    
                if (!resp.ok) {
                    return false;
                }
    
                displaySnackbar('success', "Related work item removed.");
                onUnrelated();

                return true;
            } catch {
                return false;
            } finally {
                setWorking(false);
                
                setUnrelateState({
                    id: 0,
                    show: false
                });    
            }
        })();
    };

    const DialogUI = (
        <DeleteDialog 
            titleOverride="Unrelate Work Item"            
            contentOverride="Are you sure you want to unrelate this work item?"
            actionName="Unrelate"
            open={unrelateState.show}
            onCancel={closeUnrelate}
            onDelete={confirmUnrelate}
            working={working}
        />
    );

    return [
        unrelateEntry,
        DialogUI
    ];
};

export default useUnrelateWorkItem;